import type { FederatedProvider } from './types'

/**
 * What the portal must remember across a federated sign-in redirect.
 *
 * The IdP's callback redirects to a template it holds in its own configuration,
 * carrying only `code` and `state`. Which provider to exchange the code with,
 * and where the person was originally heading, are therefore ours to keep — and
 * they have to survive a full page load, so they go in sessionStorage rather
 * than in React state.
 *
 * sessionStorage, not localStorage: a sign-in that began in this tab should not
 * be completable by another one.
 */

const storageKey = 'passgenau.auth.federated'

export interface FederatedLogin {
  provider: FederatedProvider
  returnTo: string | undefined
}

interface StoredFederatedLogin extends FederatedLogin {
  state: string
}

export const rememberFederatedLogin = (
  state: string,
  login: { provider: FederatedProvider; returnTo?: string }
): void => {
  const record: StoredFederatedLogin = {
    state,
    provider: login.provider,
    returnTo: login.returnTo,
  }
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(record))
  } catch {
    // Without storage the callback cannot tell which provider to exchange with,
    // and will say so rather than guessing.
  }
}

/**
 * Recover and consume the pending sign-in, if the state matches the one we
 * started. A mismatch means this callback belongs to a different attempt.
 */
export const takeFederatedLogin = (state: string): FederatedLogin | undefined => {
  let raw: string | null
  try {
    raw = window.sessionStorage.getItem(storageKey)
    window.sessionStorage.removeItem(storageKey)
  } catch {
    return undefined
  }
  if (!raw) return undefined

  try {
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return undefined

    const record = parsed as Record<string, unknown>
    if (record.state !== state) return undefined
    if (record.provider !== 'microsoft' && record.provider !== 'google') return undefined

    return {
      provider: record.provider,
      returnTo: typeof record.returnTo === 'string' ? record.returnTo : undefined,
    }
  } catch {
    return undefined
  }
}
